import { cn } from "@roman/shared/utils";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { ThumbaGiftPointIcon } from "@roman/shared/components/Icons";
import { AvatarIcon } from "@roman/shared/components/Icons/AvatarIcon";
import LoadingSpinner from "@roman/shared/components/LoadingSpinner";
import { useAuthStore } from "@roman/shared/store/auth.store";
import { getPlatform } from "@roman/shared/utils/platform";
import { LogOutIcon } from "lucide-react";
import { createDemoUser, isDemoMode } from "../../utils/demoUser";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { DemoModeButton } from "./DemoModeButton";
import { DownloadButton } from "./DownloadButton";
import { MobileAppBanner } from "./MobileAppBanner";
import { PointsButton } from "./PointsButton";
import { RedeemablePointsHeader } from "./RedeemablePointsHeader";

export const Header = () => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const authState = useAuthStore((state) => state.authState);
  const logout = useAuthStore((state) => state.logout);
  const [demoUser, setDemoUser] = useState(user);

  // Set demo user if demo mode is enabled and no real user
  useEffect(() => {
    if (isDemoMode() && !user) {
      setDemoUser(createDemoUser());
    } else {
      setDemoUser(user);
    }
  }, [user]);

  const handleLogout = () => {
    logout(toast, () => {
      window.location.href = "/login";
    });
  };

  const displayUser = demoUser;
  const platform = getPlatform();

  return (
    <>
      <DemoModeButton />
      <div className="sticky top-0 z-[50] w-full" id="main-header">
        <header
          className={cn(
            "flex flex-col items-center w-full bg-white/90 backdrop-blur-xl overflow-hidden border-b border-[#E7EBEE]",
          )}
        >
          {platform !== "ios" && <MobileAppBanner />}
          {/* Header content */}
          <div className="flex flex-row justify-between items-center h-full w-full max-w-[1000px] px-[20px] py-[10px]">
            <div
              className="md:flex flex-col items-center justify-center cursor-pointer"
              onClick={() => {
                navigate("/");
              }}
            >
              {platform === "pc" || !displayUser ? (
                <img src="/assets/logo.svg" alt="logo" className="h-10" onClick={() => navigate("/")} />
              ) : (
                <img src="/assets/mobile-logo.svg" alt="logo" className="h-10" onClick={() => navigate("/")} />
              )}
            </div>
            {!displayUser && platform !== "pc" && (
              <div className="flex items-center gap-4">
                <DownloadButton />
              </div>
            )}
            {/* {!displayUser && platform === "pc" && <LoginButton />} */}
            {displayUser && (
              <div className="flex items-center gap-2">
                <RedeemablePointsHeader user={displayUser} />
                <PointsButton user={displayUser} />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <div
                      className={cn(
                        "flex justify-center items-center rounded-full bg-secondary cursor-pointer transition-all ease-out hover:scale-110 hover:shadow-md active:scale-95 active:shadow-sm",
                        "h-[36px] md:h-[40px] w-[36px] md:w-[40px]",
                      )}
                    >
                      {displayUser?.photoUrl ? (
                        <img
                          src={displayUser.photoUrl}
                          alt={displayUser.name.substring(0, 1)}
                          className="rounded-full"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <AvatarIcon className="text-secondary" />
                      )}
                    </div>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-auto pb-2 rounded-[16px]">
                    {isDemoMode() && (
                      <>
                        <DropdownMenuItem disabled className="text-s text-muted-foreground">
                          MENU
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-[#E7EBEE]" />
                      </>
                    )}
                    <DropdownMenuItem onClick={() => navigate("/withdrawal")} className="cursor-pointer px-2">
                      <ThumbaGiftPointIcon className="w-[24px] h-[24px]" />
                      <span className="text-md font-medium">マイル引換え</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-[#E7EBEE]" />
                    <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive px-2">
                      <div className="w-[24px] h-[24px]">
                        <LogOutIcon width={24} height={24} />
                      </div>
                      {authState === "loggingOut" ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <span className="text-md font-medium">ログアウト</span>
                      )}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>
        </header>
      </div>
    </>
  );
};
